/**
 * Что происходит с задачей в GitLab: ветки, merge request'ы, пайплайны и
 * поднятый стенд.
 *
 * Всё приезжает одной ручкой: трекер держит у себя зеркало вебхуков, и
 * спрашивать GitLab по каждой строке — это пять запросов на карточку и
 * пятнадцать на доску.
 */
import { gitlabApi } from '@/api'

import type {
  V1DevOpsAction,
  V1IssueBranch,
  V1IssueMergeRequest,
  V1Pipeline,
} from '@/api/clients/tracker-gitlab'

/** Статусы пайплайна, после которых он уже не изменится */
const FINAL_STATUSES = ['success', 'failed', 'canceled', 'skipped', 'manual']

export interface DevBranch {
  id: string
  projectPath: string
  name: string
  url: string
}

export interface DevMergeRequest {
  id: string
  projectPath: string
  iid: string
  title: string
  /** `opened`, `merged`, `closed` — как их называет сам GitLab */
  state: string
  draft: boolean
  sourceBranch: string
  url: string
}

export interface DevPipeline {
  id: string
  projectPath: string
  ref: string
  status: string
  url: string
  durationSeconds: number
}

/** Фича-стенд задачи: адрес и то, чем закончилась его поднятие */
export interface DevStand {
  id: string
  host: string
  status: string
  isRemoved: boolean
}

export interface DevInfo {
  branches: DevBranch[]
  mergeRequests: DevMergeRequest[]
  pipelines: DevPipeline[]
  stands: DevStand[]
}

const toBranch = (branch: V1IssueBranch): DevBranch => ({
  id: branch.id ?? '',
  projectPath: branch.gitlabProjectPath ?? '',
  name: branch.branchName ?? '',
  url: branch.webUrl ?? '',
})

const toMergeRequest = (request: V1IssueMergeRequest): DevMergeRequest => ({
  id: request.id ?? '',
  projectPath: request.gitlabProjectPath ?? '',
  iid: request.mrIid ?? '',
  title: request.title ?? '',
  state: request.state ?? '',
  draft: Boolean(request.draft),
  sourceBranch: request.sourceBranch ?? '',
  url: request.webUrl ?? '',
})

const toPipeline = (pipeline: V1Pipeline): DevPipeline => ({
  id: pipeline.pipelineId ?? '',
  projectPath: pipeline.gitlabProjectPath ?? '',
  ref: pipeline.ref ?? '',
  status: pipeline.status ?? '',
  url: pipeline.webUrl ?? '',
  durationSeconds: Number(pipeline.durationSeconds ?? 0),
})

/**
 * Стенд собирается из журнала действий: своей сущности у него нет, есть
 * «подняли» и «погасили». Живым считается тот, который подняли и не убрали.
 */
const toStands = (actions: V1DevOpsAction[]): DevStand[] => {
  const removed = new Set(
    actions
      .filter((action) => action.actionType === 'DEVOPS_ACTION_TYPE_FEATURE_ENV_DELETE')
      .map((action) => action.target ?? ''),
  )

  return actions
    .filter((action) => action.actionType === 'DEVOPS_ACTION_TYPE_FEATURE_ENV_CREATE')
    .map((action) => ({
      id: action.id ?? '',
      host: action.target ?? '',
      status: action.status ?? '',
      isRemoved: removed.has(action.target ?? ''),
    }))
}

export const getDevInfo = async (issueId: string): Promise<DevInfo> => {
  const { data } = await gitlabApi.gitLabServiceGetIssueDevInfo(issueId)
  const info = data.devInfo ?? {}

  return {
    branches: (info.branches ?? []).map(toBranch),
    mergeRequests: (info.mergeRequests ?? []).map(toMergeRequest),
    pipelines: (info.pipelines ?? []).map(toPipeline),
    stands: toStands(info.devopsActions ?? []),
  }
}

/**
 * Пайплайн строки — последний прогон по её ветке в её же репозитории.
 *
 * Прогонов по ветке бывает много, и показывать первый попавшийся значит
 * показывать позавчерашний: сервис отдаёт их новыми вперёд.
 */
export const pipelineFor = (
  pipelines: DevPipeline[],
  projectPath: string,
  ref: string,
): DevPipeline | null =>
  pipelines.find(
    (pipeline) => pipeline.projectPath === projectPath && pipeline.ref === ref,
  ) ?? null

/** Есть ли ещё что-то, что вот-вот изменится само */
export const hasLivePipeline = (pipelines: DevPipeline[]): boolean =>
  pipelines.some((pipeline) => !FINAL_STATUSES.includes(pipeline.status))

/**
 * Длительность прогона — «4m 12s», как в макете.
 *
 * Не через локали: это техническая величина рядом с номером прогона, и «4 мин
 * 12 с» в моноширинной строке ломает её ширину на всех языках по-разному.
 */
export const formatDuration = (seconds: number): string => {
  if (seconds <= 0) return ''

  const minutes = Math.floor(seconds / 60)
  const rest = Math.round(seconds % 60)

  return minutes ? `${minutes}m ${String(rest).padStart(2, '0')}s` : `${rest}s`
}
